import { useEffect, useState, useCallback } from "react";
import { apiFetch } from "../context/AuthContext";

const B    = "#2C1A0E";
const ROSE = "#C9847A";
const fmtR = (n) => `₹${Number(n||0).toLocaleString("en-IN")}`;
const pct  = (a, p) => p > 0 ? Math.round((a/p)*100) : null;

const MONTHS_FY27 = [
  "Apr-26","May-26","Jun-26","Jul-26","Aug-26","Sep-26",
  "Oct-26","Nov-26","Dec-26","Jan-27","Feb-27","Mar-27"
];
const MONTHS_ALL = [
  "Apr-25","May-25","Jun-25","Jul-25","Aug-25","Sep-25",
  "Oct-25","Nov-25","Dec-25","Jan-26","Feb-26","Mar-26",
  ...MONTHS_FY27
];

const LINE_COLORS = {
  "B2C – Direct":          "#C9847A",
  "B2C – Affiliate":       "#2C6E49",
  "B2C – Pop-ups":         "#8B5CF6",
  "B2C – Workshops":       "#0EA5E9",
  "B2C – Bulk Order":      "#F4A261",
  "B2B – Corporate Gifting":"#1877F2",
  "B2B – Workshops":       "#D97706",
  "B2B – Bulk Order":      "#7C3AED",
};

export default function RevenuePage() {
  const [data,      setData]      = useState({ by_month: [], rows: [] });
  const [loading,   setLoading]   = useState(true);
  const [dateFrom,  setDateFrom]  = useState("Apr-26");
  const [dateTo,    setDateTo]    = useState("Mar-27");
  const [view,      setView]      = useState("chart"); // chart | table | breakdown

  const load = useCallback(async () => {
    setLoading(true);
    const r = await apiFetch(`/api/revenue?date_from=${dateFrom}&date_to=${dateTo}`);
    if (r.ok) setData(await r.json());
    setLoading(false);
  }, [dateFrom, dateTo]);

  useEffect(() => { load(); }, [load]);

  const months    = data.by_month || [];
  const totalPlan = months.reduce((s, m) => s + m.plan,   0);
  const totalAct  = months.reduce((s, m) => s + m.actual, 0);
  const maxVal    = Math.max(...months.map(m => Math.max(m.plan, m.actual)), 1);

  // Group rows by line_item for breakdown
  const byLine = {};
  (data.rows || []).forEach(r => {
    if (!byLine[r.line_item]) byLine[r.line_item] = { plan: 0, actual: 0 };
    byLine[r.line_item].plan   += r.plan_amount   || 0;
    byLine[r.line_item].actual += r.actual_amount || 0;
  });

  return (
    <div style={{ padding: "28px 28px 80px", fontFamily: "Inter, sans-serif" }}>
      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 24 }}>
        <div>
          <h1 style={{ fontFamily: "'Cormorant Garamond',Georgia,serif",
                       color: B, fontSize: 26, fontWeight: 700, margin: 0 }}>
            📈 Revenue
          </h1>
          <p style={{ color: "#888", fontSize: 13, marginTop: 4 }}>
            Online (Shopify) + Offline (Zoho) · Plan vs Actual
          </p>
        </div>
        {/* Date range */}
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <select value={dateFrom} onChange={e => setDateFrom(e.target.value)} style={sel}>
            {MONTHS_ALL.map(m => <option key={m}>{m}</option>)}
          </select>
          <span style={{ color: "#aaa" }}>→</span>
          <select value={dateTo} onChange={e => setDateTo(e.target.value)} style={sel}>
            {MONTHS_ALL.map(m => <option key={m}>{m}</option>)}
          </select>
        </div>
      </div>

      {/* KPI strip */}
      <div style={{ display: "flex", gap: 14, marginBottom: 24, flexWrap: "wrap" }}>
        {[
          { label: "Plan Total",   value: fmtR(totalPlan) },
          { label: "Actual Total", value: fmtR(totalAct), color: totalAct >= totalPlan ? "#15803D" : ROSE },
          { label: "Achievement",  value: pct(totalAct, totalPlan) != null ? `${pct(totalAct, totalPlan)}%` : "—",
            color: pct(totalAct, totalPlan) >= 100 ? "#15803D" : pct(totalAct, totalPlan) >= 70 ? "#92400E" : "#DC2626" },
          { label: "Gap",          value: fmtR(totalPlan - totalAct),
            color: totalAct >= totalPlan ? "#15803D" : "#DC2626" },
          { label: "Months",       value: months.length },
        ].map(({ label, value, color }) => (
          <div key={label} style={kpiCard}>
            <div style={{ fontSize: 11, color: "#888", marginBottom: 4 }}>{label}</div>
            <div style={{ fontSize: 20, fontWeight: 700, color: color || B }}>{value}</div>
          </div>
        ))}
      </div>

      {/* View tabs */}
      <div style={{ display: "flex", gap: 0, marginBottom: 20, borderBottom: "2px solid #EDE0D4" }}>
        {[["chart","📊 Chart"],["table","📋 Monthly Table"],["breakdown","🏷️ By Stream"]].map(([id, label]) => (
          <button key={id} onClick={() => setView(id)} style={{
            background: "none", border: "none", padding: "8px 20px",
            fontSize: 13, fontWeight: view === id ? 700 : 400,
            color: view === id ? B : "#888",
            borderBottom: view === id ? `2px solid ${B}` : "2px solid transparent",
            marginBottom: -2, cursor: "pointer" }}>{label}</button>
        ))}
      </div>

      {loading && <div style={{ color: "#aaa", fontSize: 13 }}>Loading…</div>}

      {/* ── Chart view ── */}
      {!loading && view === "chart" && (
        <div>
          <div style={{ display: "flex", gap: 16, marginBottom: 8, fontSize: 12 }}>
            <span><span style={{ display:"inline-block", width:12, height:12, background:"#EDE0D4", borderRadius:2, marginRight:6 }}/>Plan</span>
            <span><span style={{ display:"inline-block", width:12, height:12, background:ROSE, borderRadius:2, marginRight:6 }}/>Actual</span>
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "flex-end", height: 220, padding: "0 4px" }}>
            {months.map(m => {
              const planH   = Math.round((m.plan   / maxVal) * 200);
              const actualH = Math.round((m.actual / maxVal) * 200);
              const achPct  = pct(m.actual, m.plan);
              return (
                <div key={m.month} style={{ flex: 1, display: "flex", flexDirection: "column",
                                            alignItems: "center", gap: 2 }}>
                  <div style={{ fontSize: 9, color: achPct >= 100 ? "#15803D" : achPct >= 70 ? "#92400E" : "#DC2626",
                                fontWeight: 600 }}>
                    {achPct != null ? `${achPct}%` : ""}
                  </div>
                  <div style={{ display: "flex", gap: 2, alignItems: "flex-end", height: 200 }}>
                    <div style={{ width: 14, height: planH, background: "#EDE0D4", borderRadius: "2px 2px 0 0" }}
                         title={`Plan: ${fmtR(m.plan)}`} />
                    <div style={{ width: 14, height: actualH,
                                  background: m.actual >= m.plan ? "#2C6E49" : ROSE,
                                  borderRadius: "2px 2px 0 0" }}
                         title={`Actual: ${fmtR(m.actual)}`} />
                  </div>
                  <div style={{ fontSize: 9, color: "#888", marginTop: 4, textAlign: "center" }}>{m.month}</div>
                </div>
              );
            })}
          </div>

          {/* Stream breakdown bars */}
          <div style={{ marginTop: 32 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: B, marginBottom: 12 }}>By Revenue Stream</div>
            {Object.entries(byLine).sort((a,b) => b[1].actual - a[1].actual).map(([line, vals]) => {
              const barW = maxVal > 0 ? Math.round((vals.actual / totalPlan) * 100) : 0;
              return (
                <div key={line} style={{ marginBottom: 10 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, marginBottom: 3 }}>
                    <span style={{ color: B, fontWeight: 500 }}>{line}</span>
                    <span style={{ color: "#888" }}>{fmtR(vals.actual)} / {fmtR(vals.plan)}</span>
                  </div>
                  <div style={{ background: "#F0EBE3", borderRadius: 4, height: 8, overflow: "hidden" }}>
                    <div style={{ height: "100%", width: `${barW}%`,
                                  background: LINE_COLORS[line] || ROSE, borderRadius: 4 }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ── Monthly table ── */}
      {!loading && view === "table" && (
        <table style={tbl}>
          <thead>
            <tr style={{ background: "#F7F5F2" }}>
              <th style={th}>Month</th>
              <th style={{ ...th, textAlign: "right" }}>Plan</th>
              <th style={{ ...th, textAlign: "right" }}>Actual</th>
              <th style={{ ...th, textAlign: "right" }}>Achievement</th>
              <th style={{ ...th, textAlign: "right" }}>Gap</th>
            </tr>
          </thead>
          <tbody>
            {months.map(m => {
              const achv = pct(m.actual, m.plan);
              const gap  = m.plan - m.actual;
              return (
                <tr key={m.month} style={{ borderBottom: "1px solid #F0E8E0" }}>
                  <td style={{ ...td, fontWeight: 600 }}>{m.month}</td>
                  <td style={{ ...td, textAlign: "right" }}>{fmtR(m.plan)}</td>
                  <td style={{ ...td, textAlign: "right", fontWeight: 600,
                               color: m.actual >= m.plan ? "#15803D" : "#333" }}>
                    {fmtR(m.actual)}
                    {(m.shopify_actual > 0 || m.zoho_actual > 0) && (
                      <div style={{ fontSize: 10, color: "#aaa", fontWeight: 400, marginTop: 2 }}>
                        {m.shopify_actual > 0 && <span>🛍️{fmtR(m.shopify_actual)} </span>}
                        {m.zoho_actual > 0 && <span>🔗{fmtR(m.zoho_actual)}</span>}
                      </div>
                    )}
                  </td>
                  <td style={{ ...td, textAlign: "right" }}>
                    {achv != null && (
                      <span style={{
                        background: achv >= 100 ? "#DCFCE7" : achv >= 70 ? "#FEF9C3" : "#FEE2E2",
                        color: achv >= 100 ? "#15803D" : achv >= 70 ? "#854D0E" : "#DC2626",
                        borderRadius: 4, padding: "2px 8px", fontSize: 12, fontWeight: 600 }}>{achv}%</span>
                    )}
                  </td>
                  <td style={{ ...td, textAlign: "right",
                               color: gap <= 0 ? "#15803D" : "#DC2626" }}>
                    {gap <= 0 ? `+${fmtR(-gap)}` : `-${fmtR(gap)}`}
                  </td>
                </tr>
              );
            })}
            <tr style={{ background: "#F7F5F2", fontWeight: 700 }}>
              <td style={td}>Total</td>
              <td style={{ ...td, textAlign: "right" }}>{fmtR(totalPlan)}</td>
              <td style={{ ...td, textAlign: "right", color: totalAct >= totalPlan ? "#15803D" : "#333" }}>
                {fmtR(totalAct)}
              </td>
              <td style={{ ...td, textAlign: "right" }}>
                {pct(totalAct, totalPlan) != null && `${pct(totalAct, totalPlan)}%`}
              </td>
              <td style={{ ...td, textAlign: "right",
                           color: totalAct >= totalPlan ? "#15803D" : "#DC2626" }}>
                {fmtR(Math.abs(totalPlan - totalAct))}
              </td>
            </tr>
          </tbody>
        </table>
      )}

      {/* ── By stream breakdown ── */}
      {!loading && view === "breakdown" && (
        <table style={tbl}>
          <thead>
            <tr style={{ background: "#F7F5F2" }}>
              <th style={th}>Revenue Stream</th>
              <th style={{ ...th, textAlign: "right" }}>Plan</th>
              <th style={{ ...th, textAlign: "right" }}>Actual</th>
              <th style={{ ...th, textAlign: "right" }}>Achievement</th>
              <th style={{ ...th, textAlign: "right" }}>% of Total Actual</th>
            </tr>
          </thead>
          <tbody>
            {Object.entries(byLine).sort((a,b) => b[1].actual - a[1].actual).map(([line, vals]) => {
              const achv  = pct(vals.actual, vals.plan);
              const share = totalAct > 0 ? Math.round((vals.actual / totalAct) * 100) : 0;
              return (
                <tr key={line} style={{ borderBottom: "1px solid #F0E8E0" }}>
                  <td style={td}>
                    <span style={{ display:"inline-block", width:10, height:10, borderRadius:2,
                                   background: LINE_COLORS[line] || "#ccc", marginRight:8 }}/>
                    {line}
                  </td>
                  <td style={{ ...td, textAlign: "right" }}>{fmtR(vals.plan)}</td>
                  <td style={{ ...td, textAlign: "right", fontWeight: 600,
                               color: vals.actual >= vals.plan ? "#15803D" : "#333" }}>
                    {fmtR(vals.actual)}
                  </td>
                  <td style={{ ...td, textAlign: "right" }}>
                    {achv != null && (
                      <span style={{
                        background: achv >= 100 ? "#DCFCE7" : achv >= 70 ? "#FEF9C3" : "#FEE2E2",
                        color: achv >= 100 ? "#15803D" : achv >= 70 ? "#854D0E" : "#DC2626",
                        borderRadius: 4, padding: "2px 8px", fontSize: 12, fontWeight: 600 }}>{achv}%</span>
                    )}
                  </td>
                  <td style={{ ...td, textAlign: "right" }}>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 8 }}>
                      <div style={{ width: 60, background: "#F0EBE3", borderRadius: 3, height: 6 }}>
                        <div style={{ width: `${share}%`, height: "100%",
                                      background: LINE_COLORS[line] || ROSE, borderRadius: 3 }} />
                      </div>
                      <span style={{ fontSize: 12, color: "#555", minWidth: 28 }}>{share}%</span>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}

const kpiCard = { background:"#fff", border:"1px solid #EDE0D4", borderRadius:12,
                  padding:"14px 18px", flex:1, minWidth:120 };
const tbl  = { width:"100%", borderCollapse:"collapse", fontSize:13 };
const th   = { padding:"9px 12px", textAlign:"left", fontWeight:600, color:"#666",
               fontSize:11, textTransform:"uppercase", letterSpacing:0.5,
               borderBottom:"2px solid #EDE0D4" };
const td   = { padding:"10px 12px", verticalAlign:"middle" };
const sel  = { border:"1px solid #DDD4C8", borderRadius:6, padding:"5px 10px",
               fontSize:12, color:B, background:"#FAF7F2" };
